import { useEffect, useState, useCallback } from "react";
import { FaUserCircle, FaUserClock, FaUserTimes, FaCrown, FaUser, FaShieldAlt, FaEdit, FaCheck, FaTimes, FaEye } from "react-icons/fa";
import { IoCheckmarkCircleSharp } from "react-icons/io5";
import { MdEmail, MdSecurity } from "react-icons/md";
import { toast } from "react-toastify";
import { Tooltip } from "react-tooltip";
import { getUsersOfDesign, updateDesignAccessForUser, deleteUser, revokeUserAccess } from "../../api/userApi";
import { USER_ROLE, USER_STATUS } from "../../constants";
import { useAuth } from "../../hooks/useAuth";
import { UserGrantedForDesign } from "../../types";
import { UserRoleUpdate } from "../../types/auth-data";
import { DesignInvitationModalProps } from "../../types/modal";
import { checkIsSuperAdmin, formatRoleName } from "../../utils/auth-utils";
import { IoIosRemoveCircle } from "react-icons/io";
import { RiDeleteBin6Line } from "react-icons/ri";
import ModernDropdown from "../ModernDropdown";

export default function DesignUserManagementModal(
  props: Readonly<DesignInvitationModalProps>
) {
  const [isLoading, setIsLoading] = useState(false);
  const [usersList, setUsersList] = useState<UserGrantedForDesign[]>([]);
  const [userRoles, setUserRoles] = useState<UserRoleUpdate[]>([]);
  const [editingUser, setEditingUser] = useState<string | null>(null);
  const [userToDelete, setUserToDelete] = useState<UserGrantedForDesign | null>(null);
  const [userToRevoke, setUserToRevoke] = useState<UserGrantedForDesign | null>(null);

  const { userData } = useAuth();

  // Handle backdrop click
  const handleBackdropClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isLoading) {
      props.onCancel();
    }
  }, [props, isLoading]);

  // Handle escape key
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape' && !isLoading) {
      props.onCancel();
    }
  }, [props, isLoading]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [handleKeyDown]);

  // load the users who already has access for this design
  useEffect(() => {
    const fetchUsersOfDesign = async () => {
      setIsLoading(true);
      try {
        const response = await getUsersOfDesign({ design_id: props.design_id });

        if (response.data.statusCode !== 200) {
          throw new Error(response.data.message);
        }

        setUsersList(response.data.data);
        setUserRoles(
          response.data.data.map((user) => ({ id: user._id, role: user.role }))
        );
      } catch (error) {
        console.error(error);
        toast.error("Could not load users who has access to this design.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchUsersOfDesign();
  }, [props.design_id]);

  const getRoleStateForUser = (user: UserGrantedForDesign) => {
    const stateUserRole = userRoles.find(
      (userRoleRef) => userRoleRef.id === user._id
    );
    return stateUserRole ? stateUserRole.role : USER_ROLE.Viewer;
  };

  const updateRoleStateForUser = (
    user: UserGrantedForDesign,
    newRoleForUser: USER_ROLE
  ) => {
    setUserRoles((prev) => {
      const updatedUserRoleList = [...prev];
      const roleUpdate = updatedUserRoleList.find(
        (roleUpdateRef) => roleUpdateRef.id === user._id
      );
      if (roleUpdate) {
        roleUpdate.role = newRoleForUser;
      }
      return updatedUserRoleList;
    });
  };

  const getIsUserRoleChanged = (user: UserGrantedForDesign) => {
    const newRoleForUser = userRoles.find(
      (userRoleRef) => userRoleRef.id === user._id
    )?.role;
    return { isRoleChanged: user.role !== newRoleForUser, newRoleForUser };
  };

  const updateUserRole = async (user: UserGrantedForDesign) => {
    try {
      const { newRoleForUser, isRoleChanged } = getIsUserRoleChanged(user);
      if (!newRoleForUser || !isRoleChanged) {
        console.log("User' role not changed.");
        return;
      }

      setIsLoading(true);
      const response = await updateDesignAccessForUser({
        email: user.email,
        design_id: props.design_id,
        role: newRoleForUser,
      });

      setUsersList((prev) => {
        const updatedUserList = [...prev];
        const userToUpdate = updatedUserList.find(
          (userListRef) => userListRef._id === user._id
        );
        if (userToUpdate) {
          userToUpdate.role = newRoleForUser;
        }
        return updatedUserList;
      });

      if (response.data.statusCode !== 200) {
        throw new Error(response.data.message);
      }

      setEditingUser(null);
      toast.success("User role updated successfully!");
    } catch (error) {
      console.error(error);
      toast.error("Error while updating user role");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteUser = async (user: UserGrantedForDesign) => {
    try {
      setIsLoading(true);
      const response = await deleteUser({
        email: user.email,
        design_id: props.design_id,
      });

      if (response.data.statusCode !== 200) {
        throw new Error(response.data.message);
      }
      setUsersList((prev) => prev.filter((u) => u._id !== user._id));
      
      toast.success(`User ${user.email} has been permanently deleted from the system!`);
    } catch (error) {
      console.error(error);
      toast.error("Error while deleting user");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRevokeUserAccess = async (user: UserGrantedForDesign) => {
    try {
      setIsLoading(true);
      const response = await revokeUserAccess({
        email: user.email,
        design_id: props.design_id,
      });

      if (response.data.statusCode !== 200) {
        throw new Error(response.data.message);
      }
      setUsersList((prev) => prev.filter((u) => u._id !== user._id));
      
      toast.success(`Access to this design has been revoked for user ${user.email}!`);
    } catch (error) {
      console.error(error);
      toast.error("Error while revoking user access");
    } finally {
      setIsLoading(false);
    }
  };

  const getUserStatusInfo = (status: USER_STATUS) => {
    switch (status) {
      case USER_STATUS.ACTIVE:
        return {
          icon: <FaUserCircle />,
          label: "Active",
          className: "status-active",
          description: "User has design access"
        };
      case USER_STATUS.PENDING:
        return {
          icon: <FaUserClock />,
          label: "Pending",
          className: "status-pending",
          description: "Invitation sent, awaiting response"
        };
      case USER_STATUS.DELETED:
        return {
          icon: <FaUserTimes />,
          label: "Deleted",
          className: "status-deleted",
          description: "User access has been removed"
        };
      default:
        return {
          icon: <FaUser />,
          label: "Unknown",
          className: "status-unknown",
          description: ""
        };
    }
  };

  const getRoleInfo = (role: USER_ROLE) => {
    switch (role) {
      case USER_ROLE.SUPER_ADMIN:
        return {
          icon: <FaCrown />,
          label: "Super Admin",
          className: "role-super-admin",
          description: "Full system access and control",
          color: "var(--primary-600)"
        };
      case USER_ROLE.ADMIN:
        return {
          icon: <FaShieldAlt />,
          label: "Admin",
          className: "role-admin",
          description: "Manage users and content",
          color: "var(--info-600)"
        };
      case USER_ROLE.USER:
        return {
          icon: <FaUser />,
          label: "User",
          className: "role-user",
          description: "View and edit content",
          color: "var(--neutral-600)"
        };
      case USER_ROLE.Viewer:
        return {
          icon: <FaEye />,
          label: "Viewer",
          className: "role-viewer",
          description: "View content",
          color: "var(--neutral-600)"
        };
      default:
        return {
          icon: <FaEye />,
          label: "Viewer",
          className: "role-viewer",
          description: "Standard access",
          color: "var(--neutral-600)"
        };
    }
  };

  const handleStartEditing = (userId: string) => {
    setEditingUser(userId);
  };

  const handleCancelEditing = () => {
    setEditingUser(null);
    // Reset role changes
    setUserRoles(
      usersList.map((user) => ({ id: user._id, role: user.role }))
    );
  };

  const canEditUser = (user: UserGrantedForDesign) => {
    if (!userData) return false;
    if (checkIsSuperAdmin(userData.role)) return true;
    if (userData.role === USER_ROLE.ADMIN && user.role !== USER_ROLE.SUPER_ADMIN) return true;
    return false;
  };

  return (
    <div className="modal-overlay" onClick={handleBackdropClick}>
      <div className="modal-content user-management-modal">
        <div className="modal-header">
          <div className="header-content">
            <h2 className="modal-title">
              <MdSecurity className="title-icon" />
              User Access Management
            </h2>
            <div className="modal-subtitle">
              <span className="design-name">{props.design_name}</span>
              <span className="subtitle-text">Manage user permissions and roles</span>
            </div>
          </div>
        </div>

        <div className="modal-body">
          {isLoading && (
            <div className="loading-overlay">
              <div className="spinner-lg"></div>
            </div>
          )}

          {usersList.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">
                <FaUserCircle />
              </div>
              <h3>No Users Found</h3>
              <p>No users have been invited to this design yet.</p>
            </div>
          ) : (
            <div className="users-container">
              <div className="users-header">
                <div className="users-summary">
                  <span className="users-count">
                    <strong>{usersList.length}</strong> user{usersList.length !== 1 ? 's' : ''} with access
                  </span>
                  <div className="status-legend">
                    <div className="legend-item">
                      <div className="legend-dot status-active"></div>
                      <span>Active</span>
                    </div>
                    <div className="legend-item">
                      <div className="legend-dot status-pending"></div>
                      <span>Pending</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="users-grid">
                {usersList.map((user) => {
                  const statusInfo = getUserStatusInfo(user.status);
                  const roleInfo = getRoleInfo(user.role);
                  const currentRole = getRoleStateForUser(user);
                  const currentRoleInfo = getRoleInfo(currentRole || USER_ROLE.Viewer);
                  const isEditing = editingUser === user._id;
                  const hasChanges = getIsUserRoleChanged(user).isRoleChanged;
                  const canEdit = canEditUser(user);

                  return (
                    <div key={user._id} className={`user-card ${isEditing ? 'editing' : ''} ${statusInfo.className}`}>
                      <div className="user-header">
                        <div className="user-avatar">
                          <div className={`avatar-icon ${statusInfo.className}`}>
                            {statusInfo.icon}
                          </div>
                          <div className="user-info">
                            <div className="user-email">
                              <MdEmail className="email-icon" />
                              {user.email}
                            </div>
                            <div className="user-status">
                              <span className={`status-badge ${statusInfo.className}`}>
                                {statusInfo.label}
                              </span>
                              <span className="status-description">
                                {statusInfo.description}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        {canEdit && user.status === USER_STATUS.ACTIVE && (
                          <div className="user-actions-header">
                            {!isEditing ? (
                              <button
                                className="btn btn-ghost btn-sm edit-btn"
                                onClick={() => handleStartEditing(user._id)}
                                data-tooltip-id={`edit_${user._id}`}
                                data-tooltip-content="Edit user role"
                              >
                                <FaEdit />
                                <Tooltip id={`edit_${user._id}`} />
                              </button>
                            ) : (
                              <div className="edit-controls">
                                <button
                                  className="btn btn-ghost btn-sm"
                                  onClick={handleCancelEditing}
                                  data-tooltip-id={`cancel_${user._id}`}
                                  data-tooltip-content="Cancel changes"
                                >
                                  <FaTimes />
                                  <Tooltip id={`cancel_${user._id}`} />
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="user-content">
                        <div className="role-section">
                          <div className="section-header">
                            <h4 className="section-title">
                              <MdSecurity />
                              Role & Permissions
                            </h4>
                          </div>
                          
                          {!isEditing ? (
                            <div className="role-display-card">
                              <div className="role-icon-container" style={{ color: roleInfo.color }}>
                                {roleInfo.icon}
                              </div>
                              <div className="role-details">
                                <div className="role-name">{roleInfo.label}</div>
                                <div className="role-description">{roleInfo.description}</div>
                              </div>
                            </div>
                          ) : (
                            <div className="role-edit-section">
                              <div className="role-selector-container">
                                <label className="form-label">Select New Role</label>
                                <ModernDropdown
                                  value={getRoleStateForUser(user)}
                                  onChange={(value) => updateRoleStateForUser(user, value as USER_ROLE)}
                                  options={userData?.can_assign_roles?.map((role) => ({
                                    value: role,
                                    label: formatRoleName(role)
                                  })) || []}
                                  placeholder="Select role"
                                  disabled={isLoading}
                                  isRoleDropdown={true}
                                  className="role-dropdown"
                                />
                              </div>
                              
                              {hasChanges && (
                                <div className="role-preview">
                                  <div className="preview-header">
                                    <span>New Role Preview:</span>
                                  </div>
                                  <div className="role-display-card preview">
                                    <div className="role-icon-container" style={{ color: currentRoleInfo.color }}>
                                      {currentRoleInfo.icon}
                                    </div>
                                    <div className="role-details">
                                      <div className="role-name">{currentRoleInfo.label}</div>
                                      <div className="role-description">{currentRoleInfo.description}</div>
                                    </div>
                                  </div>
                                </div>
                              )}
                              
                              <div className="edit-actions">
                                <button
                                  className="btn btn-primary btn-sm"
                                  onClick={() => updateUserRole(user)}
                                  disabled={isLoading || !hasChanges}
                                >
                                  <FaCheck />
                                  Save Changes
                                </button>
                              </div>
                            </div>
                          )}
                        </div>

                        {canEdit && (
                          <div className="danger-zone">
                            <div className="section-header">
                              <h4 className="section-title danger">
                                Danger Zone
                              </h4>
                            </div>
                            <div className="danger-actions">
                              <button
                                className="btn btn-outline btn-sm danger-btn"
                                onClick={() => setUserToRevoke(user)}
                                disabled={isLoading}
                                data-tooltip-id={`revoke_${user._id}`}
                                data-tooltip-content="Revoke user access"
                              >
                                <IoIosRemoveCircle />
                                Revoke Access
                                <Tooltip id={`revoke_${user._id}`} />
                              </button>
                              
                                                             {userData && checkIsSuperAdmin(userData.role) && (
                                <button
                                  className="btn btn-outline btn-sm danger-btn"
                                  onClick={() => setUserToDelete(user)}
                                  disabled={isLoading}
                                  data-tooltip-id={`delete_${user._id}`}
                                  data-tooltip-content="Delete user permanently"
                                >
                                  <RiDeleteBin6Line />
                                  Delete User
                                  <Tooltip id={`delete_${user._id}`} />
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <div className="footer-info">
            <span className="info-text">
              Changes are saved immediately upon confirmation
            </span>
          </div>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={props.onCancel}
            disabled={isLoading}
          >
            Close
          </button>
        </div>
      </div>

      {/* Delete User Confirmation Modal */}
      {userToDelete && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setUserToDelete(null)}>
          <div className="modal-content confirmation-modal">
            <div className="modal-header">
              <h3 className="modal-title danger">Delete User</h3>
            </div>
            <div className="modal-body">
              <div className="confirmation-content">
                <div className="warning-icon">
                  <RiDeleteBin6Line />
                </div>
                <div className="confirmation-text">
                  <p>Are you sure you want to permanently delete this user?</p>
                  <p className="user-info">
                    <strong>User:</strong> {userToDelete.email}
                  </p>
                  <p className="warning-text">
                    This action cannot be undone. The user record will be completely removed from the database, including all their access permissions and data.
                  </p>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setUserToDelete(null)}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => {
                  handleDeleteUser(userToDelete);
                  setUserToDelete(null);
                }}
                disabled={isLoading}
              >
                {isLoading ? "Deleting..." : "Delete User"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Revoke Access Confirmation Modal */}
      {userToRevoke && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setUserToRevoke(null)}>
          <div className="modal-content confirmation-modal">
            <div className="modal-header">
              <h3 className="modal-title warning">Revoke Access</h3>
            </div>
            <div className="modal-body">
              <div className="confirmation-content">
                <div className="warning-icon">
                  <IoIosRemoveCircle />
                </div>
                <div className="confirmation-text">
                  <p>Are you sure you want to revoke access for this user?</p>
                  <p className="user-info">
                    <strong>User:</strong> {userToRevoke.email}
                  </p>
                  <p className="warning-text">
                    The design ID will be removed from the user's has_access array. The user account will remain active but will lose access to this specific design.
                  </p>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setUserToRevoke(null)}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-warning"
                onClick={() => {
                  handleRevokeUserAccess(userToRevoke);
                  setUserToRevoke(null);
                }}
                disabled={isLoading}
              >
                {isLoading ? "Revoking..." : "Revoke Access"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
